import React, { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Flex } from "@nubeteck/components";
import { AcademicRecordContainer } from "./report-container.styled";
import { Empty, Form, Spin, Skeleton } from "antd";
import {
  FlexPDFButtonsStyled,
  SpinnerContainerStyled,
} from "src/pages/student-pages/cart/cart.styled";
import {
  ButtonAlternativeStyled,
  ButtonPrimaryStyled,
} from "src/components/custom/buttons/buttons.styled";
import { useGetReportByIdMutation } from "src/services";
import ConfigurationInput from "src/pages/admin-pages/generalSettings/settingsInput";
import { IReport } from "src/Interfaces/reports";
import { FlexStyled } from "src/components/registration/register-confirmation/confirmation.styled";
import { getFormData } from "src/utils/files-util";
import { useMediaQuery } from "src/hooks";
import { _translateValue } from "src/pages/admin-pages/translation-gestor/translateValue";
import { useLocale } from "src/i18n/localeContext";
import { getReportsPage } from "../../../Api/content";
import { StrapiError } from "../../../pages/StrapiError";
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const SCREEN_NAME = "PantallaReportContainer";

function ReportContainer({ reporte }: { reporte: IReport | undefined }) {
  const [form] = Form.useForm<any>();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { locale } = useLocale();
  const [reportsPage, setReportsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [getReport, { data: report, isLoading: isLoadingReport, error: errorReport }] =
    useGetReportByIdMutation();
  const [PDFPages, setPDFPages] = React.useState<number>(0);
  const [keepRendering, setKeepRendering] = React.useState<number>(1);
  const [translatedData, setTranslatedData] = React.useState<{
    campos: { [key: string]: string };
    descripciones: { [key: string]: string };
    opciones: { [key: string]: string };
  }>({
    campos: {},
    descripciones: {},
    opciones: {},
  });

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setReportsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getReportsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setReportsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setReportsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);


  const generateReport = () => {
    form
      .validateFields(reporte?.parametros?.map((pa) => pa.reporteParametroNombre))
      .then((values) => {
        if (values) {
          getReport({ reporteId: reporte?.reporteId, values: getFormData(values) });
        }
      });
  };

  const onDocLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPDFPages(numPages);
  };

  const getPdfPages = () => {
    const pages: JSX.Element[] = [];
    for (let index = 1; index <= PDFPages; index++) {
      pages.push(
        <Page
          pageNumber={index}
          width={window.innerWidth / 1.6}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          renderMode="canvas"
        />,
      );
    }
    return pages;
  };

  useEffect(() => {
    setPDFPages(0);
    reporte?.parametros?.forEach((field) => {
      form.setFieldValue(field.reporteParametroNombre, field.valor);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reporte]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = reporte?.parametros && Array.isArray(reporte.parametros) && reporte.parametros.length > 0;
      if (!hasData) {
        setTranslatedData({ campos: {}, descripciones: {}, opciones: {} });
        return;
      }

      const camposTranslations: { [key: string]: string | null } = {};
      const descripcionesTranslations: { [key: string]: string | null } = {};
      const opcionesTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      (reporte?.parametros ?? []).forEach((field: any) => {
        if (field.reporteParametroCampo && !camposTranslations.hasOwnProperty(field.reporteParametroCampo)) {
          camposTranslations[field.reporteParametroCampo] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, field.reporteParametroCampo, locale).then((translated) => {
              camposTranslations[field.reporteParametroCampo] = translated;
            })
          );
        }
        if (field.reporteParametroDescripcion && !descripcionesTranslations.hasOwnProperty(field.reporteParametroDescripcion)) {
          descripcionesTranslations[field.reporteParametroDescripcion] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, field.reporteParametroDescripcion, locale).then((translated) => {
              descripcionesTranslations[field.reporteParametroDescripcion] = translated;
            })
          );
        }
        // Traducir opciones
        if (field.opciones && Array.isArray(field.opciones)) {
          field.opciones.forEach((op: any) => {
            if (op.opcion && !opcionesTranslations.hasOwnProperty(op.opcion)) {
              opcionesTranslations[op.opcion] = null;
              translationPromises.push(
                _translateValue(SCREEN_NAME, op.opcion, locale).then((translated) => {
                  opcionesTranslations[op.opcion] = translated;
                })
              );
            }
          });
        }
      });

      await Promise.all(translationPromises);
      setTranslatedData({
        campos: camposTranslations as { [key: string]: string },
        descripciones: descripcionesTranslations as { [key: string]: string },
        opciones: opcionesTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [reporte?.parametros, locale]);

  const cleanChildren = (parametroId: number) => {
    const fieldChildren = reporte?.parametros?.filter(
      (p) => p.parametroReporteFiltroId === parametroId,
    );

    fieldChildren?.forEach((child) => {
      form.resetFields([child.reporteParametroNombre]);
    });
  };

  const getParameterOptions = (opciones: any[] | null, parametroFiltroId: number) => {
    const parametroReferenciaNombre = reporte?.parametros?.find(
      (p) => p.reporteParametroId === parametroFiltroId,
    )?.reporteParametroNombre;
    const parametroReferenciaValor = parametroReferenciaNombre
      ? form.getFieldValue(parametroReferenciaNombre)
      : null;

    const opcionesCascada = parametroReferenciaValor
      ? opciones?.filter((op) => op.filtro === parametroReferenciaValor)
      : opciones;

    return opcionesCascada && opcionesCascada?.length > 0
      ? opcionesCascada?.map((op) => ({ opcion: translatedData.opciones[op.opcion] || op.opcion, id: op.opcion }))
      : [];
  };

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setReportsPage(null);
      }}
    />;
  }

  return reporte?.reporteId ? (
    <>
      <Form form={form}>
        <Flex
          style={{
            gap: "15px",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <FlexStyled
            style={{ gap: isMobile ? 0 : 12, marginTop: isMobile ? 0 : 50, flexWrap: "wrap" }}
          >
            {keepRendering
              ? reporte?.parametros?.map((field) => {
                  return (
                    <Form.Item
                      key={field.reporteParametroId}
                      rules={[{ required: field.reporteParametroRequerido }]}
                      label={translatedData.campos[field.reporteParametroCampo] || field.reporteParametroCampo}
                      name={field.reporteParametroNombre}
                    >
                      <ConfigurationInput
                        data={{
                          id: field.reporteParametroId,
                          label: translatedData.campos[field.reporteParametroCampo] || field.reporteParametroCampo,
                          nombre: field.reporteParametroNombre,
                          descripcion: translatedData.descripciones[field.reporteParametroDescripcion] || field.reporteParametroDescripcion,
                          opciones: getParameterOptions(
                            field.opciones,
                            field.parametroReporteFiltroId,
                          ),
                          tipo: field.reporteParametroTipoNombre,
                          requerido: field.reporteParametroRequerido,
                          valor: form.getFieldValue(field.reporteParametroNombre),
                        }}
                        onChange={(value) => {
                          cleanChildren(field.reporteParametroId);
                          form.setFieldValue(field.reporteParametroNombre, value);
                          setKeepRendering(keepRendering + 1);
                        }}
                      />
                    </Form.Item>
                  );
                })
              : null}
          </FlexStyled>
          <FlexPDFButtonsStyled>
            {isLoadingContent ? (
              <Skeleton.Button active size="large" style={{ width: 150 }} />
            ) : (
              <ButtonPrimaryStyled onClick={generateReport}>{reportsPage?.generar_reporte || "Generar reporte"}</ButtonPrimaryStyled>
            )}
            {PDFPages > 0 && (
              isLoadingContent ? (
                <Skeleton.Button active size="large" style={{ width: 120 }} />
              ) : (
                <ButtonAlternativeStyled
                  loading={isLoadingReport}
                  type="link"
                  href={`data:application/pdf;base64,${report?.data}`}
                  download={`Reporte ${reporte?.reporteNombre}`}
                >
                  {reportsPage?.descargar_pdf || "Descargar PDF"}
                </ButtonAlternativeStyled>
              )
            )}
          </FlexPDFButtonsStyled>
        </Flex>
      </Form>
      <AcademicRecordContainer>
        {isLoadingReport && (
          <SpinnerContainerStyled>
            <Spin spinning={isLoadingReport} size="large" />
          </SpinnerContainerStyled>
        )}
        <Document
          file={`data:application/pdf;base64,${report?.data}`}
          onLoadSuccess={onDocLoadSuccess}
          loading={
            errorReport
              ? reportsPage?.error_generar_reporte || "Ha ocurrido un error generando el reporte."
              : !isLoadingReport
              ? (reporte?.parametros ?? []).length > 0
                ? reportsPage?.complete_parametros || "Complete los parametros para generar el reporte."
                : reportsPage?.haga_click_generar || "Haga clík en Generar reporte"
              : reportsPage?.generando_reporte || "...Generando reporte"
          }
        >
          {getPdfPages()}
        </Document>
      </AcademicRecordContainer>
    </>
  ) : (
    <>
      <Empty description={reportsPage?.seleccione_reporte || "Seleccione un reporte"} />
    </>
  );
}

export default ReportContainer;
